import { FileText, Download, Edit, Trash2, AlertTriangle } from "lucide-react";
import { descargarDocumento } from "@/lib/descargarDocumento";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { api } from "@/App";
import { toast } from "sonner";

function DocumentosList({ documentos, casoId, onUpdate }) {
  const [editingDoc, setEditingDoc] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [docToDelete, setDocToDelete] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");

  const handleDownload = async (doc) => {
    await descargarDocumento(casoId, doc);
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setEditForm({
      descripcion_breve: doc.descripcion_breve || '',
      remitente: doc.remitente || '',
      destinatario: doc.destinatario || '',
      asunto: doc.asunto || '',
      cita_relevante: doc.cita_relevante || ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/casos/${casoId}/documentos/${editingDoc.id}`, editForm);
      toast.success('Documento actualizado');
      setEditingDoc(null);
      onUpdate();
    } catch (error) {
      toast.error('Error actualizando documento');
      console.error(error);
    }
  };

  const handleDelete = (doc) => {
    setDocToDelete(doc);
  };

  const confirmDelete = async () => {
    if (!deletePassword) {
      toast.error('Ingresa la clave de seguridad');
      return;
    }

    try {
      await api.delete(`/casos/${casoId}/documentos/${docToDelete.id}?password=${deletePassword}`);
      toast.success('Documento eliminado');
      setDocToDelete(null);
      setDeletePassword("");
      onUpdate();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Clave incorrecta');
      } else {
        toast.error('Error eliminando documento');
      }
      console.error(error);
    }
  };

  const getTipoColor = (tipo) => {
    const colores = {
      correo_pdf: 'bg-blue-100 text-blue-800',
      contrato: 'bg-green-100 text-green-800',
      factura: 'bg-yellow-100 text-yellow-800',
      informe: 'bg-purple-100 text-purple-800',
      otro: 'bg-gray-100 text-gray-800'
    };
    return colores[tipo] || 'bg-gray-100 text-gray-800';
  };

  if (documentos.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500 text-sm">
        No hay documentos adjuntos
      </div>
    );
  }

  return (
    <>
      <div className="divide-y" data-testid="documentos-list">
        {documentos.map((doc) => (
          <div
            key={doc.id}
            className="p-3 hover:bg-gray-50 transition-colors group"
            data-testid={`documento-item-${doc.id}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-semibold text-blue-900">DOC N° {doc.numero_documento}</span>
              </div>
              <span className={`document-badge ${getTipoColor(doc.tipo_documento)}`}>
                {doc.tipo_documento.replace('_', ' ')}
              </span>
            </div>
            
            <h4 className="text-sm font-medium text-gray-900 mb-1">{doc.nombre_archivo_original}</h4>
            {doc.archivo_existe === false && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 border border-red-300 rounded px-1.5 py-0.5 mb-1" data-testid={`archivo-perdido-badge-${doc.id}`}>
                <AlertTriangle className="h-3 w-3" />
                Archivo perdido — resubir
              </span>
            )}
            <p className="text-xs text-gray-600 mb-2">{doc.descripcion_breve}</p>
            
            {doc.cita_relevante && (
              <div className="bg-blue-50 border-l-2 border-blue-600 p-2 mb-2">
                <p className="text-xs text-gray-700 italic">"{doc.cita_relevante}"</p>
              </div>
            )}
            
            <div className="flex items-center gap-2 mt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(doc)}
                data-testid={`download-doc-${doc.id}`}
              >
                <Download className="h-3 w-3 mr-1" />
                Descargar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(doc)}
                data-testid={`edit-doc-${doc.id}`}
              >
                <Edit className="h-3 w-3 mr-1" />
                Editar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(doc)}
                data-testid={`delete-doc-${doc.id}`}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Eliminar
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Dialog de edición */}
      <Dialog open={!!editingDoc} onOpenChange={() => setEditingDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Documento</DialogTitle>
            <DialogDescription>Actualiza la información extraída del documento</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Descripción</Label>
              <Input
                value={editForm.descripcion_breve || ''}
                onChange={(e) => setEditForm({...editForm, descripcion_breve: e.target.value})}
              />
            </div>
            <div>
              <Label>Remitente</Label>
              <Input
                value={editForm.remitente || ''}
                onChange={(e) => setEditForm({...editForm, remitente: e.target.value})}
                placeholder="Nombre del remitente (si es correo)"
              />
            </div>
            <div>
              <Label>Destinatario</Label>
              <Input
                value={editForm.destinatario || ''}
                onChange={(e) => setEditForm({...editForm, destinatario: e.target.value})}
                placeholder="Nombre del destinatario (si es correo)"
              />
            </div>
            <div>
              <Label>Asunto</Label>
              <Input
                value={editForm.asunto || ''}
                onChange={(e) => setEditForm({...editForm, asunto: e.target.value})}
                placeholder="Asunto del correo"
              />
            </div>
            <div>
              <Label>Cita Relevante</Label>
              <Textarea
                value={editForm.cita_relevante || ''}
                onChange={(e) => setEditForm({...editForm, cita_relevante: e.target.value})}
                placeholder="Fragmento textual relevante del documento"
                rows={4}
              />
            </div>
            <Button onClick={handleSaveEdit} className="w-full">Guardar Cambios</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog open={docToDelete !== null} onOpenChange={(open) => {
        if (!open) {
          setDocToDelete(null);
          setDeletePassword("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación de Documento</DialogTitle>
            <DialogDescription>
              Para eliminar este documento, ingresa la clave de seguridad.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="delete-doc-password">Clave de Seguridad</Label>
            <Input
              id="delete-doc-password"
              type="password"
              placeholder="Ingresa la clave"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  confirmDelete();
                }
              }}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDocToDelete(null);
                setDeletePassword("");
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
            >
              Eliminar Documento
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default DocumentosList;
